import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useToast } from '~/design-system/atom/use-toast'
import {
  getOrgApiKey,
  getOrgProjectWebhook,
  postOrgApiKeyRegenerate,
  postOrgProjectWebhookRefresh,
  postOrgProjectWebhookUpdate,
} from '~/generated/client'

export const QUERY_KEYS_SETTING = {
  GET_API_KEYS: 'GET_API_KEYS',
  GET_ORG_API_KEY: 'GET_ORG_API_KEY',
  CLIENT_INFO: 'CLIENT_INFO',
  GET_TRIGGER_WEBHOOK_URL_QUERY_KEY: 'GET_TRIGGER_WEBHOOK_URL_QUERY_KEY',
  GET_WEBHOOK_SECRET_QUERY_KEY: 'GET_WEBHOOK_SECRET_QUERY_KEY',
  TEAM_MEMBERS: 'TEAM_MEMBERS',
  GET_EVENT_WEBHOOK_URL_QUERY_KEY: 'GET_EVENT_WEBHOOK_URL_QUERY_KEY',
}

const STALE_TIME = 1000 * 60 * 5 // 5 minutes

export const useEventWebhookUrlQuery = (type: 'trigger' | 'event') => {
  return useQuery({
    queryKey: [QUERY_KEYS_SETTING.GET_EVENT_WEBHOOK_URL_QUERY_KEY],
    queryFn: () =>
      getOrgProjectWebhook({
        type,
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
  })
}

export function useGetOrgApiKeyQuery() {
  return useQuery({
    queryKey: [QUERY_KEYS_SETTING.GET_ORG_API_KEY],
    queryFn: getOrgApiKey,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
  })
}

export function useRegenerateOrgApiKeyMutation({
  onSuccess,
  onError,
}: {
  onSuccess: () => void
  onError: () => void
}) {
  return useMutation(postOrgApiKeyRegenerate, {
    onSuccess,
    onError,
  })
}
// WEBHOOKS

export const useUpdateWebhookURL = () => {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      type,
      webhookURL,
    }: {
      type: 'trigger' | 'event'
      webhookURL: string
    }) => {
      return postOrgProjectWebhookUpdate({
        requestBody: {
          type,
          webhook_url: webhookURL,
        },
      })
    },
    onSuccess: (_, variables) => {
      if (variables.webhookURL === '') {
        toast({
          title: 'Success',
          description:
            'Webhook URL was deleted successfully and will no longer trigger any events',
          variant: 'success',
        })
      } else {
        toast({
          title: 'Success',
          description: 'Webhook URL was updated successfully',
          variant: 'success',
        })
      }
      queryClient.invalidateQueries([
        QUERY_KEYS_SETTING.GET_TRIGGER_WEBHOOK_URL_QUERY_KEY,
      ])
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Error setting webhook URL',
        variant: 'destructive',
      })
    },
  })
}

export const useSetEventWebhookURL = () => {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: postOrgProjectWebhookUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERY_KEYS_SETTING.GET_EVENT_WEBHOOK_URL_QUERY_KEY,
      ])
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Error setting event webhook URL',
        variant: 'destructive',
      })
    },
  })
}

export function useGetTriggerWebhookURL(type: 'trigger' | 'event') {
  return useQuery({
    queryKey: [QUERY_KEYS_SETTING.GET_TRIGGER_WEBHOOK_URL_QUERY_KEY],
    queryFn: () =>
      getOrgProjectWebhook({
        type,
      }),
  })
}

export function useGetWebhookSecret() {
  return useQuery({
    queryKey: [QUERY_KEYS_SETTING.GET_WEBHOOK_SECRET_QUERY_KEY],
    queryFn: () => {
      return getOrgProjectWebhook({
        type: 'trigger',
      })
    },
  })
}

export const useRegenerateWebhookSecret = () => {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => {
      return postOrgProjectWebhookRefresh()
    },
    onSuccess: async () => {
      queryClient.invalidateQueries([
        QUERY_KEYS_SETTING.GET_WEBHOOK_SECRET_QUERY_KEY,
      ])
      toast({
        title: 'Success',
        description: 'Webhook secret was regenerated successfully',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to regenerate webhook secret',
        variant: 'destructive',
      })
    },
  })
}
